/**
 * QA Assignment page — admin/supervisor batch-creates draft evaluations.
 *
 * Uses:
 *   POST /api/qa/assignments  { form_id, criteria, evaluator_ids, dry_run }
 *   GET  /api/reports/filters/options   (for dropdowns)
 *   GET  /api/qa/forms                  (for form list)
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { qaApi, reportsApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

export default function AssignPage() {
  const opts = useQuery({
    queryKey: ['reports','filters','options'],
    queryFn:  () => reportsApi.filterOptions(),
    staleTime: 60_000,
  });
  const forms = useQuery({
    queryKey: ['qa','forms'],
    queryFn:  () => qaApi.listForms(),
    staleTime: 60_000,
  });

  const [form_id, setForm] = useState('');
  const [queue_ids, setQueues] = useState([]);
  const [agent_extensions, setAgents] = useState([]);
  const [evaluator_ids, setEvaluators] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minDur, setMinDur] = useState('');
  const [maxDur, setMaxDur] = useState('');
  const [onlyInbound, setOnlyInbound] = useState(false);
  const [sample, setSample] = useState(100);
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState(null);

  const mut = useMutation({
    mutationFn: (payload) => qaApi.createAssignments(payload),
    onSuccess: (r) => setResult(r),
    onError: (e) => setResult({ error: e?.detail || e?.message || 'Failed' }),
  });

  const submit = (dry_run) => {
    setResult(null);
    if (!form_id || evaluator_ids.length === 0) {
      setResult({ error: 'Pick a form and at least one evaluator.' });
      return;
    }
    const criteria = {
      queue_ids: queue_ids.length ? queue_ids : null,
      agent_extensions: agent_extensions.length ? agent_extensions : null,
      date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
      date_to:   dateTo   ? new Date(dateTo).toISOString()   : null,
      min_duration_sec: minDur === '' ? null : Number(minDur),
      max_duration_sec: maxDur === '' ? null : Number(maxDur),
      only_inbound: onlyInbound,
      random_sample_pct: Number(sample) || 100,
      limit: Number(limit) || 100,
    };
    mut.mutate({ form_id, criteria, evaluator_ids, dry_run });
  };

  const users = opts.data?.users || [];
  const queues = opts.data?.queues || [];
  const formList = forms.data || [];

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">QA - assign evaluations</h2>
        <HelpButton moduleKey="qa_assignments" />
      </div>

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-4 space-y-3 bg-surface-panel dark:bg-surface-dark-panel">
        <div>
          <label className="block text-xs text-surface-muted mb-1">Form</label>
          <select value={form_id} onChange={(e) => setForm(e.target.value)}
            className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full">
            <option value="">— pick a form —</option>
            {formList.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-surface-muted mb-1">Queues (empty = all)</label>
            <select multiple value={queue_ids} onChange={(e) => setQueues(Array.from(e.target.selectedOptions, o => o.value))}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full h-32">
              {queues.map(q => <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Agents (empty = all)</label>
            <select multiple value={agent_extensions} onChange={(e) => setAgents(Array.from(e.target.selectedOptions, o => o.value))}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full h-32">
              {users.map(u => <option key={u.extension} value={u.extension}>{u.extension} · {u.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-surface-muted mb-1">Date from</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Date to (exclusive)</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-surface-muted mb-1">Min duration (sec)</label>
            <input type="number" value={minDur} onChange={(e) => setMinDur(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Max duration (sec)</label>
            <input type="number" value={maxDur} onChange={(e) => setMaxDur(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Sample %</label>
            <input type="number" min="1" max="100" value={sample} onChange={(e) => setSample(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Max recordings</label>
            <input type="number" min="1" max="2000" value={limit} onChange={(e) => setLimit(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
        </div>

        <label className="text-sm inline-flex items-center gap-2">
          <input type="checkbox" checked={onlyInbound} onChange={(e) => setOnlyInbound(e.target.checked)} />
          Only inbound (queue) calls
        </label>

        <div>
          <label className="block text-xs text-surface-muted mb-1">Evaluators (pick one or more)</label>
          <select multiple value={evaluator_ids} onChange={(e) => setEvaluators(Array.from(e.target.selectedOptions, o => o.value))}
            className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full h-40">
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.extension}</option>)}
          </select>
        </div>

        <div className="flex gap-2">
          <button onClick={() => submit(true)} disabled={mut.isPending}
            className="px-3 py-1.5 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            {mut.isPending ? '…' : 'Preview (dry run)'}
          </button>
          <button onClick={() => submit(false)} disabled={mut.isPending}
            className="px-3 py-1.5 text-sm rounded-thin bg-continuum-600 text-white hover:bg-continuum-700 disabled:opacity-40">
            Assign
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4 rounded-thin border border-surface-border dark:border-surface-dark-border p-3 text-sm bg-surface-panel dark:bg-surface-dark-panel">
          {result.error ? (
            <div className="text-accent-red">Error: {result.error}</div>
          ) : (
            <>
              <div><strong>Matched recordings:</strong> {result.matched_recordings}</div>
              <div><strong>{result.dry_run ? 'Would create' : 'Created'}:</strong> {result.evaluations_created} evaluations</div>
              <div className="mt-1 text-xs text-surface-muted">
                Skipped (no agent): {result.skipped_no_agent} · Skipped (dedup): {result.skipped_dedup}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
